import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import GoogleIcon from "@mui/icons-material/Google";
import AppleIcon from "@mui/icons-material/Apple";
import config from "../Config/config";

const PAGE_TITLE = "Pickup Scheduling";
const EXPLORER_URL = "https://explorer.listernaut.com/";
const EXPLORER_LOGO_URL = "https://storage.googleapis.com/listalotimages/public/logo192.png";
const ACCOUNT_MANAGER = {
  name: "Jeff Kaplan",
  email: "jeff@auctionfuel.com",
  phone: "(317) 798-4459",
};

const normalizeEmailParam = (v) =>
  String(v || "")
    .trim()
    .replace(/^['"]+|['"]+$/g, "")
    .toLowerCase();
const normalizeOrgParam = (v) =>
  String(v || "")
    .trim()
    .replace(/^['"]+|['"]+$/g, "")
    .replace(/[^a-zA-Z0-9_-]/g, "");
const normalizeRegistrationParam = (v) =>
  String(v || "")
    .trim()
    .replace(/^['"]+|['"]+$/g, "");

const RequiredMark = () => (
  <Box component="span" sx={{ color: "#dc2626", fontWeight: 900 }}>
    *
  </Box>
);

const renderRequiredLabel = (label, complete) => (
  <>
    {label}
    {!complete ? " " : ""}
    {!complete ? <RequiredMark /> : null}
  </>
);

const fmtDateTime = (ms) => {
  if (!ms || !Number.isFinite(Number(ms))) return "—";
  try {
    return new Date(Number(ms)).toLocaleString();
  } catch {
    return "—";
  }
};

const fmtDate = (ms) => {
  if (!ms || !Number.isFinite(Number(ms))) return "—";
  try {
    return new Date(Number(ms)).toLocaleDateString([], {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

const fmtTime = (ms) => {
  if (!ms || !Number.isFinite(Number(ms))) return "—";
  try {
    return new Date(Number(ms)).toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const localDateKeyFromMs = (ms) => {
  if (!ms || !Number.isFinite(Number(ms))) return "";
  const d = new Date(Number(ms));
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const localTimeKeyFromMs = (ms) => {
  if (!ms || !Number.isFinite(Number(ms))) return "";
  const d = new Date(Number(ms));
  const hour = String(d.getHours()).padStart(2, "0");
  const minute = String(d.getMinutes()).padStart(2, "0");
  return `${hour}:${minute}`;
};

const buildPickupDateOptions = (slots = [], location = "") => {
  const targetLocation = String(location || "").trim().toLowerCase();
  if (!targetLocation) return [];
  const map = new Map();
  nextSlotsOrEmpty(slots).forEach((slot) => {
    if (String(slot?.location || "").trim().toLowerCase() !== targetLocation) return;
    const startAtMs = Number(slot?.startAtMs || 0) || 0;
    if (!startAtMs) return;
    const dateKey = localDateKeyFromMs(startAtMs);
    if (!dateKey) return;
    const existing = map.get(dateKey);
    if (!existing || startAtMs < existing.firstMs) {
      map.set(dateKey, {
        dateKey,
        firstMs: startAtMs,
        label: fmtDate(startAtMs),
      });
    }
  });
  return Array.from(map.values()).sort((a, b) => Number(a.firstMs || 0) - Number(b.firstMs || 0));
};

const buildPickupTimeOptionsForDate = (slots = [], location = "", dateKey = "") => {
  const targetLocation = String(location || "").trim().toLowerCase();
  if (!targetLocation || !dateKey) return [];
  const rows = [];
  const seen = new Set();
  nextSlotsOrEmpty(slots).forEach((slot) => {
    if (String(slot?.location || "").trim().toLowerCase() !== targetLocation) return;
    const slotId = String(slot?.slotId || "").trim();
    const startAtMs = Number(slot?.startAtMs || 0) || 0;
    const endAtMs = Number(slot?.endAtMs || 0) || 0;
    if (!slotId || !startAtMs || !endAtMs || endAtMs < startAtMs) return;
    if (localDateKeyFromMs(startAtMs) !== dateKey) return;
    for (let atMs = startAtMs; atMs <= endAtMs; atMs += 15 * 60 * 1000) {
      const key = `${slotId}:${atMs}`;
      if (seen.has(key)) continue;
      seen.add(key);
      rows.push({
        value: String(atMs),
        appointmentAtMs: atMs,
        dateKey,
        timeKey: localTimeKeyFromMs(atMs),
        slotId,
        startAtMs,
        endAtMs,
        slotLabel: String(slot?.label || "").trim(),
        label: fmtTime(atMs),
      });
    }
  });
  return rows.sort((a, b) => Number(a.appointmentAtMs || 0) - Number(b.appointmentAtMs || 0));
};

const mobileChipSx = {
  borderRadius: 999,
  fontWeight: 800,
  maxWidth: "100%",
  height: "auto",
  "& .MuiChip-label": {
    display: "block",
    whiteSpace: "normal",
    lineHeight: 1.2,
    paddingTop: "6px",
    paddingBottom: "6px",
  },
};

const buildGoogleMapsUrl = (address = "") => {
  const safeAddress = String(address || "").trim();
  if (!safeAddress) return "";
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(safeAddress)}`;
};

const buildAppleMapsUrl = (address = "") => {
  const safeAddress = String(address || "").trim();
  if (!safeAddress) return "";
  return `https://maps.apple.com/?q=${encodeURIComponent(safeAddress)}`;
};

const mapIconButtonSx = {
  width: 38,
  height: 38,
  minWidth: 38,
  borderRadius: "50%",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  color: "#fff",
  boxShadow: "0 10px 24px rgba(15,23,42,0.10)",
};

const stepCardStyle = (active) => ({
  borderRadius: 2.5,
  border: active ? "1px solid rgba(37,99,235,0.24)" : "1px solid rgba(148,163,184,0.18)",
  background: active ? "linear-gradient(180deg,#eff6ff 0%,#ffffff 100%)" : "#fff",
  boxShadow: active ? "0 18px 40px rgba(37,99,235,0.08)" : "none",
});

export default function PickupSchedulingPage() {
  const [email, setEmail] = useState("");
  const [orgId, setOrgId] = useState("");
  const [orgName, setOrgName] = useState("");
  const [registrationId, setRegistrationId] = useState("");
  const [auctionId, setAuctionId] = useState("");
  const [auctionName, setAuctionName] = useState("");
  const [bidderName, setBidderName] = useState("");
  const [username, setUsername] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [pickupSlots, setPickupSlots] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [locationMetadataRows, setLocationMetadataRows] = useState([]);
  const [selection, setSelection] = useState({
    location: "",
    appointmentDateKey: "",
    appointmentAtMs: 0,
    slotId: "",
    additionalInfo: "",
    startAtMs: 0,
    endAtMs: 0,
    label: "",
  });
  const [exists, setExists] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editingSelection, setEditingSelection] = useState(true);
  const [step, setStep] = useState(1);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const emailFromUrl = normalizeEmailParam(params.get("email"));
    const orgFromUrl = normalizeOrgParam(params.get("org") || params.get("orgId") || params.get("publicOrgId"));
    const registrationFromUrl = normalizeRegistrationParam(params.get("registrationId") || params.get("regId"));
    const auctionFromUrl = String(params.get("auctionId") || "").trim();
    if (!orgFromUrl) {
      setError("Missing organization key in link. Use ?org=<key>&registrationId=<docId>.");
      setLoading(false);
      return;
    }
    if (!registrationFromUrl && (!auctionFromUrl || !emailFromUrl)) {
      setError("Missing registrationId in link (or provide auctionId + email).");
      setLoading(false);
      return;
    }
    if (!registrationFromUrl && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailFromUrl)) {
      setError("Invalid email in link.");
      setLoading(false);
      return;
    }

    setOrgId(orgFromUrl);
    setEmail(emailFromUrl);
    setRegistrationId(registrationFromUrl);
    setAuctionId(auctionFromUrl);
    void loadStatus({
      org: orgFromUrl,
      email: emailFromUrl,
      registrationId: registrationFromUrl,
      auctionId: auctionFromUrl,
    });
  }, []);

  const locationMetadataByName = useMemo(() => {
    const map = new Map();
    nextSlotsOrEmpty(locationMetadataRows).forEach((row) => {
      const name = String(row?.name || "").trim();
      if (!name) return;
      const key = name.toLowerCase();
      if (map.has(key)) return;
      map.set(key, {
        name,
        address: String(row?.address || "").trim(),
      });
    });
    return map;
  }, [locationMetadataRows]);
  const getLocationAddress = useCallback(
    (name = "") => String(locationMetadataByName.get(String(name || "").trim().toLowerCase())?.address || "").trim(),
    [locationMetadataByName]
  );
  const selectedLocationAddress = useMemo(
    () => getLocationAddress(selection.location),
    [selection.location, getLocationAddress]
  );
  const selectedLocationGoogleMapsUrl = useMemo(
    () => buildGoogleMapsUrl(selectedLocationAddress),
    [selectedLocationAddress]
  );
  const selectedLocationAppleMapsUrl = useMemo(
    () => buildAppleMapsUrl(selectedLocationAddress),
    [selectedLocationAddress]
  );
  const pickupDateOptions = useMemo(
    () => buildPickupDateOptions(pickupSlots, selection.location),
    [pickupSlots, selection.location]
  );
  const pickupTimeOptions = useMemo(
    () => buildPickupTimeOptionsForDate(pickupSlots, selection.location, selection.appointmentDateKey),
    [pickupSlots, selection.location, selection.appointmentDateKey]
  );
  const hasSavedSelection = useMemo(
    () =>
      Boolean(
        String(selection.location || "").trim() &&
        String(selection.slotId || "").trim() &&
        Number(selection.appointmentAtMs || 0)
      ),
    [selection.location, selection.slotId, selection.appointmentAtMs]
  );
  const showPickupEditor = !hasSavedSelection || editingSelection;
  const normalizedAddress = String(address || "").trim();
  const normalizedPhone = String(phone || "").trim();
  const missingPickupFields = useMemo(() => {
    const missing = [];
    if (!normalizedAddress) missing.push("Address");
    if (!normalizedPhone) missing.push("Phone Number");
    if (!String(selection.location || "").trim()) missing.push("Pickup Location");
    if (!String(selection.appointmentDateKey || "").trim()) missing.push("Pickup Date");
    if (!(Number(selection.appointmentAtMs || 0) > 0) || !String(selection.slotId || "").trim()) missing.push("Pickup Time");
    return missing;
  }, [
    normalizedAddress,
    normalizedPhone,
    selection.appointmentAtMs,
    selection.appointmentDateKey,
    selection.location,
    selection.slotId,
  ]);
  const isPickupFormComplete = missingPickupFields.length === 0;
  const isContactStepComplete = Boolean(normalizedAddress && normalizedPhone);
  const isSelectionStepComplete = Boolean(
    String(selection.location || "").trim() &&
    String(selection.appointmentDateKey || "").trim() &&
    Number(selection.appointmentAtMs || 0) > 0 &&
    String(selection.slotId || "").trim()
  );

  const clearMessages = () => {
    if (error) setError("");
    if (success) setSuccess("");
  };

  const handleEditSelection = () => {
    clearMessages();
    setEditingSelection(true);
    setStep(1);
  };

  const handleContinueToPickupSelection = () => {
    if (!normalizedAddress) {
      setError("Please confirm your address first.");
      return;
    }
    if (!normalizedPhone) {
      setError("Please confirm your phone number first.");
      return;
    }
    setError("");
    setStep(2);
  };

  const handleContinueToPickupReview = () => {
    if (!selection.location) {
      setError("Please select a pickup location first.");
      return;
    }
    if (!selection.appointmentDateKey) {
      setError("Please select a pickup date first.");
      return;
    }
    if (!selection.slotId || !selection.appointmentAtMs) {
      setError("Please select a pickup time first.");
      return;
    }
    setError("");
    setStep(3);
  };

  const loadStatus = async ({
    org,
    email: emailValue,
    registrationId: registrationIdValue = "",
    auctionId: auctionIdValue = "",
  }) => {
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const { data } = await axios.get(`${config.API_BASE_URL}/pickup-scheduling/status`, {
        params: {
          org,
          ...(emailValue ? { email: emailValue } : {}),
          ...(registrationIdValue ? { registrationId: registrationIdValue } : {}),
          ...(auctionIdValue ? { auctionId: auctionIdValue } : {}),
        },
      });
      if (!data?.ok) throw new Error(data?.error || "Failed to load pickup scheduling");
      const nextSelection = data?.pickupAppointment || {};
      const rawLocations = Array.isArray(data?.pickupLocationOptions) ? data.pickupLocationOptions : [];
      const nextLocations = Array.from(
        new Set(
          [...rawLocations, String(nextSelection?.location || "").trim()].filter(Boolean)
        )
      );
      const preferredDefaultLocation = String(data?.defaultPickupLocationName || "").trim();
      const resolvedDefaultLocation =
        preferredDefaultLocation &&
        nextLocations.some(
          (locationName) => String(locationName || "").trim().toLowerCase() === preferredDefaultLocation.toLowerCase()
        )
          ? nextLocations.find(
              (locationName) => String(locationName || "").trim().toLowerCase() === preferredDefaultLocation.toLowerCase()
            ) || ""
          : "";
      const nextSlots = Array.isArray(data?.pickupSlots) ? data.pickupSlots : [];

      setExists(!!data?.exists);
      setBidderName(String(data?.bidderName || "").trim());
      setUsername(String(data?.username || "").trim());
      setAddress(String(data?.address || "").trim());
      setPhone(String(data?.phone || "").trim());
      setAuctionName(String(data?.auctionName || "").trim());
      setAuctionId(String(data?.auctionId || auctionIdValue || "").trim());
      setRegistrationId(String(data?.registrationId || registrationIdValue || "").trim());
      setEmail(String(data?.email || emailValue || "").trim().toLowerCase());
      setOrgId(String(data?.publicOrgId || org || "").trim());
      setOrgName(String(data?.orgName || "").trim());
      setPickupSlots(nextSlots);
      setLocationOptions(nextLocations);
      setLocationMetadataRows(Array.isArray(data?.pickupLocationMetadataRows) ? data.pickupLocationMetadataRows : []);
      setSelection({
        location: String(nextSelection?.location || resolvedDefaultLocation || nextLocations[0] || "").trim(),
        appointmentDateKey:
          String(nextSelection?.appointmentDateKey || "").trim() ||
          localDateKeyFromMs(nextSelection?.appointmentAtMs || nextSelection?.startAtMs || 0),
        appointmentAtMs: Number(nextSelection?.appointmentAtMs || 0) || 0,
        slotId: String(nextSelection?.slotId || "").trim(),
        additionalInfo: String(nextSelection?.additionalInfo || "").trim(),
        startAtMs: Number(nextSelection?.startAtMs || 0) || 0,
        endAtMs: Number(nextSelection?.endAtMs || 0) || 0,
        label: String(nextSelection?.label || "").trim(),
      });
      setEditingSelection(
        !Boolean(
          String(nextSelection?.location || "").trim() &&
          String(nextSelection?.slotId || "").trim() &&
          Number(nextSelection?.appointmentAtMs || 0)
        )
      );
      setStep(1);
      if (!data?.exists) setError("No registration was found for this pickup link.");
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Failed to load pickup scheduling.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!orgId) {
      setError("Missing organization key.");
      return;
    }
    if (!normalizedAddress) {
      setStep(1);
      setError("Please confirm your address.");
      return;
    }
    if (!normalizedPhone) {
      setStep(1);
      setError("Please confirm your phone number.");
      return;
    }
    if (!selection.location) {
      setStep(2);
      setError("Please select a pickup location.");
      return;
    }
    if (!selection.slotId) {
      setStep(2);
      setError("Please select a pickup date and time.");
      return;
    }
    if (!selection.appointmentAtMs) {
      setStep(2);
      setError("Please select a pickup time.");
      return;
    }

    setSubmitting(true);
    setError("");
    setSuccess("");
    try {
      const { data } = await axios.post(`${config.API_BASE_URL}/pickup-scheduling/submit`, {
        org: orgId,
        registrationId,
        auctionId,
        email,
        address: normalizedAddress,
        phone: normalizedPhone,
        location: selection.location,
        slotId: selection.slotId,
        appointmentAtMs: selection.appointmentAtMs,
        additionalInfo: selection.additionalInfo,
      });
      if (!data?.ok) throw new Error(data?.error || "Failed to save pickup scheduling.");
      const nextSelection = data?.pickupAppointment || {};
      setSelection({
        location: String(nextSelection?.location || selection.location || "").trim(),
        appointmentDateKey:
          String(nextSelection?.appointmentDateKey || selection.appointmentDateKey || "").trim() ||
          localDateKeyFromMs(nextSelection?.appointmentAtMs || nextSelection?.startAtMs || 0),
        appointmentAtMs: Number(nextSelection?.appointmentAtMs || selection.appointmentAtMs || 0) || 0,
        slotId: String(nextSelection?.slotId || selection.slotId || "").trim(),
        additionalInfo: String(nextSelection?.additionalInfo || selection.additionalInfo || "").trim(),
        startAtMs: Number(nextSelection?.startAtMs || 0) || 0,
        endAtMs: Number(nextSelection?.endAtMs || 0) || 0,
        label: String(nextSelection?.label || "").trim(),
      });
      setLocationMetadataRows(Array.isArray(data?.pickupLocationMetadataRows) ? data.pickupLocationMetadataRows : []);
      setAddress(String(data?.address || address || "").trim());
      setPhone(String(data?.phone || phone || "").trim());
      setEditingSelection(false);
      setStep(1);
      setSuccess("Pickup location, date, and time saved successfully.");
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Failed to save pickup scheduling.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(180deg,#eef2ff 0%, #f8fafc 100%)",
        py: { xs: 4, md: 7 },
        px: { xs: 1.25, sm: 2 },
      }}
    >
      <Box sx={{ maxWidth: 860, mx: "auto" }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: "1px solid rgba(99,102,241,0.16)",
            overflow: "hidden",
            boxShadow: "0 24px 80px rgba(15,23,42,0.12)",
          }}
        >
          <Box
            sx={{
              px: { xs: 1.75, sm: 2.5, md: 4 },
              py: { xs: 2.25, md: 3 },
              background: "linear-gradient(135deg,#333999 0%,#4f46e5 100%)",
              color: "#fff",
            }}
          >
            <Typography sx={{ fontSize: { xs: 26, md: 34 }, fontWeight: 900, lineHeight: 1.05 }}>
              {PAGE_TITLE}
            </Typography>
            <Typography sx={{ mt: 1, color: "rgba(255,255,255,0.88)", fontSize: { xs: 14, md: 16 } }}>
              Choose your pickup location, pickup date, and exact pickup time for this auction.
            </Typography>
          </Box>

          <Box sx={{ p: { xs: 1.5, sm: 2, md: 4 }, backgroundColor: "#f8fafc" }}>
            {loading ? (
              <Box sx={{ py: 8, textAlign: "center" }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ display: "grid", gap: 2 }}>
                {error ? <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert> : null}
                {success ? <Alert severity="success" sx={{ borderRadius: 2 }}>{success}</Alert> : null}

                <Paper sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3, border: "1px solid rgba(148,163,184,0.18)" }}>
                  <Typography sx={{ fontWeight: 900, color: "#0f172a", wordBreak: "break-word" }}>
                    {bidderName || username || email || "Bidder"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b", mt: 0.35, wordBreak: "break-word" }}>
                    {email || "—"}
                  </Typography>
                  <Box sx={{ mt: 1.25, display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {orgName ? <Chip label={orgName} sx={mobileChipSx} /> : null}
                    <Chip label={auctionName || "Auction"} sx={mobileChipSx} />
                    {selection.appointmentAtMs ? (
                      <Chip label={`Saved: ${fmtDateTime(selection.appointmentAtMs)}`} sx={mobileChipSx} />
                    ) : null}
                  </Box>
                </Paper>

                {exists ? (
                  <Paper sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3, border: "1px solid rgba(148,163,184,0.18)" }}>
                    <Typography sx={{ fontWeight: 900, color: "#0f172a", mb: 1.25 }}>
                      Pickup Details
                    </Typography>

                    {hasSavedSelection && !showPickupEditor ? (
                      <Alert
                        severity="success"
                        sx={{
                          mb: 1.5,
                          borderRadius: 2.5,
                          alignItems: "flex-start",
                          background: "linear-gradient(135deg,#ecfdf5 0%,#d1fae5 100%)",
                          border: "1px solid rgba(16,185,129,0.22)",
                          "& .MuiAlert-message": { width: "100%" },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            gap: 1.25,
                            width: "100%",
                          }}
                        >
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              sx={{
                                fontWeight: 900,
                                color: "#065f46",
                                lineHeight: 1.3,
                                fontSize: { xs: 15, sm: 16 },
                              }}
                            >
                              We got you covered. Here is your pickup time and location.
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={handleEditSelection}
                            sx={{
                              mt: -0.15,
                              color: "#065f46",
                              border: "1px solid rgba(6,95,70,0.18)",
                              backgroundColor: "rgba(255,255,255,0.55)",
                              "&:hover": { backgroundColor: "rgba(255,255,255,0.75)" },
                              alignSelf: "flex-start",
                              flexShrink: 0,
                            }}
                            aria-label="Edit pickup details"
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#7c2d12",
                            fontWeight: 900,
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 0.75,
                            mb: 0.9,
                            mt: 0.25,
                            p: 1,
                            borderRadius: 1.75,
                            backgroundColor: "rgba(255,255,255,0.45)",
                            border: "1px solid rgba(124,45,18,0.10)",
                            lineHeight: 1.45,
                          }}
                        >
                          <EditOutlinedIcon fontSize="small" />
                          If you want to edit your pickup please click on this edit icon.
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#065f46", fontWeight: 700 }}>
                          Pickup time: {fmtDateTime(selection.appointmentAtMs)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#065f46", fontWeight: 700 }}>
                          Pickup location: {selection.location || "—"}
                        </Typography>
                        {selectedLocationAddress ? (
                          <Box sx={{ display: "grid", gap: 0.65 }}>
                            <Typography variant="body2" sx={{ color: "#065f46" }}>
                              Address: {selectedLocationAddress}
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                              <Typography variant="body2" sx={{ color: "#065f46", fontWeight: 700 }}>
                                Opens maps app:
                              </Typography>
                              <Button
                                component="a"
                                href={selectedLocationGoogleMapsUrl || undefined}
                                target="_blank"
                                rel="noreferrer"
                                aria-label="Open in Google Maps"
                                sx={{
                                  ...mapIconButtonSx,
                                  background: "linear-gradient(135deg,#2563eb,#06b6d4)",
                                  "&:hover": {
                                    background: "linear-gradient(135deg,#1d4ed8,#0891b2)",
                                  },
                                }}
                              >
                                <GoogleIcon sx={{ fontSize: 20 }} />
                              </Button>
                              <Button
                                component="a"
                                href={selectedLocationAppleMapsUrl || undefined}
                                target="_blank"
                                rel="noreferrer"
                                aria-label="Open in Apple Maps"
                                sx={{
                                  ...mapIconButtonSx,
                                  background: "linear-gradient(135deg,#0f172a,#334155)",
                                  "&:hover": {
                                    background: "linear-gradient(135deg,#020617,#1e293b)",
                                  },
                                }}
                              >
                                <AppleIcon sx={{ fontSize: 20 }} />
                              </Button>
                            </Box>
                          </Box>
                        ) : null}
                      </Alert>
                    ) : null}

                    {showPickupEditor ? (
                      <>
                        {step === 1 ? (
                          <Paper sx={{ ...stepCardStyle(true), p: { xs: 2, md: 2.5 }, mb: 1.5 }}>
                            <Chip label="Step 1 of 3" sx={{ ...mobileChipSx, mb: 1.2, fontWeight: 900 }} />
                            <Typography sx={{ fontWeight: 900, color: "#111827", fontSize: 22 }}>
                              Confirm your contact details
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#475569", mt: 0.55, mb: 1.5, lineHeight: 1.7 }}>
                              Please confirm your address and phone number first so we know how to reach you if there are
                              any pickup issues or questions.
                            </Typography>
                            <Typography sx={{ fontWeight: 900, color: "#111827", mb: 0.4 }}>
                              {renderRequiredLabel("Address", !!normalizedAddress)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#475569", mb: 1 }}>
                              Please confirm the best address we should have on file for this registration. If it is not
                              correct, update it below before you save your pickup.
                            </Typography>
                            <TextField
                              label={renderRequiredLabel("Address", !!normalizedAddress)}
                              value={address}
                              onChange={(e) => {
                                clearMessages();
                                setAddress(e.target.value);
                              }}
                              fullWidth
                              multiline
                              minRows={2}
                              sx={{ background: "#fff", borderRadius: 2 }}
                            />
                            <Typography sx={{ fontWeight: 900, color: "#111827", mb: 0.4, mt: 1.25 }}>
                              {renderRequiredLabel("Phone Number", !!normalizedPhone)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#475569", mb: 1 }}>
                              Please confirm this is the best phone number to reach you if there are any pickup issues or questions.
                              If it is not correct, update it below for our records.
                            </Typography>
                            <TextField
                              label={renderRequiredLabel("Phone Number", !!normalizedPhone)}
                              value={phone}
                              onChange={(e) => {
                                clearMessages();
                                setPhone(e.target.value);
                              }}
                              fullWidth
                              sx={{ background: "#fff", borderRadius: 2 }}
                            />
                            <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                              {hasSavedSelection ? (
                                <Button
                                  onClick={() => {
                                    clearMessages();
                                    setEditingSelection(false);
                                    setStep(1);
                                  }}
                                  sx={{
                                    textTransform: "none",
                                    borderRadius: 999,
                                    px: 2.6,
                                    py: 1,
                                    fontWeight: 800,
                                    color: "#334155",
                                    background: "rgba(226,232,240,0.78)",
                                    "&:hover": { background: "rgba(203,213,225,0.92)" },
                                  }}
                                >
                                  Back
                                </Button>
                              ) : (
                                <Box />
                              )}
                              <Button
                                onClick={handleContinueToPickupSelection}
                                disabled={!isContactStepComplete}
                                sx={{
                                  textTransform: "none",
                                  borderRadius: 999,
                                  px: 3,
                                  py: 1.05,
                                  fontWeight: 900,
                                  color: "#fff",
                                  background: isContactStepComplete
                                    ? "linear-gradient(90deg,#2563eb,#1d4ed8)"
                                    : "linear-gradient(90deg,#9ca3af,#6b7280)",
                                  "&:hover": {
                                    background: isContactStepComplete
                                      ? "linear-gradient(90deg,#1d4ed8,#1e40af)"
                                      : "linear-gradient(90deg,#9ca3af,#6b7280)",
                                  },
                                }}
                              >
                                Next
                              </Button>
                            </Box>
                          </Paper>
                        ) : null}

                        {step === 2 ? (
                          <Paper sx={{ ...stepCardStyle(true), p: { xs: 2, md: 2.5 }, mb: 1.5 }}>
                            <Chip label="Step 2 of 3" sx={{ ...mobileChipSx, mb: 1.2, fontWeight: 900 }} />
                            <Typography sx={{ fontWeight: 900, color: "#111827", fontSize: 22 }}>
                              Select your pickup location, date, and time
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#475569", mt: 0.55, mb: 1.5, lineHeight: 1.7 }}>
                              Choose where you want to pick up your items and the exact time slot that works for you.
                            </Typography>
                            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.25 }}>
                              <FormControl fullWidth size="small">
                                <InputLabel id="pickup-location-label">
                                  {renderRequiredLabel("Pickup Location", !!selection.location)}
                                </InputLabel>
                                <Select
                                  labelId="pickup-location-label"
                                  label={renderRequiredLabel("Pickup Location", !!selection.location)}
                                  value={selection.location}
                                  onChange={(e) => {
                                    clearMessages();
                                    setSelection((prev) => ({
                                      ...prev,
                                      location: String(e.target.value || ""),
                                      appointmentDateKey: "",
                                      appointmentAtMs: 0,
                                      slotId: "",
                                      startAtMs: 0,
                                      endAtMs: 0,
                                      label: "",
                                    }));
                                  }}
                                  sx={{ borderRadius: 2, background: "#fff" }}
                                  MenuProps={{
                                    PaperProps: {
                                      sx: {
                                        "& .MuiMenuItem-root": {
                                          whiteSpace: "normal",
                                        },
                                      },
                                    },
                                  }}
                                >
                                  {locationOptions.length === 0 ? (
                                    <MenuItem value="" disabled>
                                      No pickup locations available
                                    </MenuItem>
                                  ) : (
                                    locationOptions.map((location) => (
                                      <MenuItem key={location} value={location}>
                                        <Box sx={{ minWidth: 0 }}>
                                          <Typography variant="body2">{location}</Typography>
                                          {getLocationAddress(location) ? (
                                            <Typography variant="caption" sx={{ color: "#64748b", display: "block" }}>
                                              {getLocationAddress(location)}
                                            </Typography>
                                          ) : null}
                                        </Box>
                                      </MenuItem>
                                    ))
                                  )}
                                </Select>
                              </FormControl>

                              <FormControl fullWidth size="small">
                                <InputLabel id="pickup-date-label">
                                  {renderRequiredLabel("Pickup Date", !!selection.appointmentDateKey)}
                                </InputLabel>
                                <Select
                                  labelId="pickup-date-label"
                                  label={renderRequiredLabel("Pickup Date", !!selection.appointmentDateKey)}
                                  value={selection.appointmentDateKey}
                                  onChange={(e) => {
                                    clearMessages();
                                    const nextDateKey = String(e.target.value || "");
                                    setSelection((prev) => ({
                                      ...prev,
                                      appointmentDateKey: nextDateKey,
                                      appointmentAtMs: 0,
                                      slotId: "",
                                      startAtMs: 0,
                                      endAtMs: 0,
                                      label: "",
                                    }));
                                  }}
                                  sx={{ borderRadius: 2, background: "#fff" }}
                                >
                                  {pickupDateOptions.length === 0 ? (
                                    <MenuItem value="" disabled>
                                      No pickup dates available
                                    </MenuItem>
                                  ) : (
                                    pickupDateOptions.map((option) => (
                                      <MenuItem key={option.dateKey} value={option.dateKey}>
                                        {option.label}
                                      </MenuItem>
                                    ))
                                  )}
                                </Select>
                              </FormControl>

                              <FormControl fullWidth size="small">
                                <InputLabel id="pickup-time-label">
                                  {renderRequiredLabel("Pickup Time", !!selection.appointmentAtMs)}
                                </InputLabel>
                                <Select
                                  labelId="pickup-time-label"
                                  label={renderRequiredLabel("Pickup Time", !!selection.appointmentAtMs)}
                                  value={selection.appointmentAtMs ? String(selection.appointmentAtMs) : ""}
                                  onChange={(e) => {
                                    clearMessages();
                                    const nextAppointmentValue = String(e.target.value || "");
                                    const nextTime =
                                      pickupTimeOptions.find((option) => option.value === nextAppointmentValue) || null;
                                    setSelection((prev) => ({
                                      ...prev,
                                      appointmentAtMs: Number(nextTime?.appointmentAtMs || 0) || 0,
                                      slotId: String(nextTime?.slotId || "").trim(),
                                      startAtMs: Number(nextTime?.startAtMs || 0) || 0,
                                      endAtMs: Number(nextTime?.endAtMs || 0) || 0,
                                      label: String(nextTime?.slotLabel || "").trim(),
                                    }));
                                  }}
                                  disabled={!selection.appointmentDateKey}
                                  sx={{ borderRadius: 2, background: "#fff" }}
                                >
                                  {!selection.appointmentDateKey ? (
                                    <MenuItem value="" disabled>
                                      Select a date first
                                    </MenuItem>
                                  ) : pickupTimeOptions.length === 0 ? (
                                    <MenuItem value="" disabled>
                                      No pickup times available
                                    </MenuItem>
                                  ) : (
                                    pickupTimeOptions.map((option) => (
                                      <MenuItem key={`${option.slotId}-${option.value}`} value={option.value}>
                                        {option.label}
                                      </MenuItem>
                                    ))
                                  )}
                                </Select>
                              </FormControl>

                              <TextField
                                label="Additional Info (Optional)"
                                multiline
                                minRows={1}
                                maxRows={6}
                                value={selection.additionalInfo}
                                onChange={(e) => {
                                  clearMessages();
                                  setSelection((prev) => ({ ...prev, additionalInfo: e.target.value }));
                                }}
                                fullWidth
                                sx={{ gridColumn: { xs: "1 / -1", md: "1 / -1" }, background: "#fff", borderRadius: 2 }}
                              />
                            </Box>

                            {selectedLocationAddress ? (
                              <Box
                                sx={{
                                  mt: 1.5,
                                  p: 1.4,
                                  borderRadius: 2.25,
                                  background: "#fff",
                                  border: "1px solid rgba(148,163,184,0.18)",
                                  display: "grid",
                                  gap: 0.65,
                                }}
                              >
                                <Typography variant="body2" sx={{ color: "#334155" }}>
                                  <b>Pickup address:</b> {selectedLocationAddress}
                                </Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                  <Typography variant="body2" sx={{ color: "#475569", fontWeight: 700 }}>
                                    Opens maps app:
                                  </Typography>
                                  <Button
                                    component="a"
                                    href={selectedLocationGoogleMapsUrl || undefined}
                                    target="_blank"
                                    rel="noreferrer"
                                    aria-label="Open in Google Maps"
                                    sx={{
                                      ...mapIconButtonSx,
                                      background: "linear-gradient(135deg,#2563eb,#06b6d4)",
                                      "&:hover": {
                                        background: "linear-gradient(135deg,#1d4ed8,#0891b2)",
                                      },
                                    }}
                                  >
                                    <GoogleIcon sx={{ fontSize: 20 }} />
                                  </Button>
                                  <Button
                                    component="a"
                                    href={selectedLocationAppleMapsUrl || undefined}
                                    target="_blank"
                                    rel="noreferrer"
                                    aria-label="Open in Apple Maps"
                                    sx={{
                                      ...mapIconButtonSx,
                                      background: "linear-gradient(135deg,#0f172a,#334155)",
                                      "&:hover": {
                                        background: "linear-gradient(135deg,#020617,#1e293b)",
                                      },
                                    }}
                                  >
                                    <AppleIcon sx={{ fontSize: 20 }} />
                                  </Button>
                                </Box>
                              </Box>
                            ) : null}

                            <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                              <Button
                                onClick={() => {
                                  clearMessages();
                                  setStep(1);
                                }}
                                sx={{
                                  textTransform: "none",
                                  borderRadius: 999,
                                  px: 2.6,
                                  py: 1,
                                  fontWeight: 800,
                                  color: "#334155",
                                  background: "rgba(226,232,240,0.78)",
                                  "&:hover": { background: "rgba(203,213,225,0.92)" },
                                }}
                              >
                                Back
                              </Button>
                              <Button
                                onClick={handleContinueToPickupReview}
                                disabled={!isSelectionStepComplete}
                                sx={{
                                  textTransform: "none",
                                  borderRadius: 999,
                                  px: 3,
                                  py: 1.05,
                                  fontWeight: 900,
                                  color: "#fff",
                                  background: isSelectionStepComplete
                                    ? "linear-gradient(90deg,#2563eb,#1d4ed8)"
                                    : "linear-gradient(90deg,#9ca3af,#6b7280)",
                                  "&:hover": {
                                    background: isSelectionStepComplete
                                      ? "linear-gradient(90deg,#1d4ed8,#1e40af)"
                                      : "linear-gradient(90deg,#9ca3af,#6b7280)",
                                  },
                                }}
                              >
                                Next
                              </Button>
                            </Box>
                          </Paper>
                        ) : null}

                        {step === 3 ? (
                          <Paper sx={{ ...stepCardStyle(true), p: { xs: 2, md: 2.5 } }}>
                            <Chip label="Step 3 of 3" sx={{ ...mobileChipSx, mb: 1.2, fontWeight: 900 }} />
                            <Typography sx={{ fontWeight: 900, color: "#111827", fontSize: 22 }}>
                              Confirm your details before saving
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#475569", mt: 0.55, mb: 1.25, lineHeight: 1.7 }}>
                              Review everything below. Any item with a <RequiredMark /> still needs to be completed before you can save.
                            </Typography>
                            <Box
                              sx={{
                                p: 1.25,
                                borderRadius: 2,
                                background: "rgba(255,255,255,0.72)",
                                border: "1px solid rgba(22,163,74,0.12)",
                              }}
                            >
                              <Box sx={{ display: "grid", gap: 0.45 }}>
                                <Typography variant="body2" sx={{ color: "#334155" }}>
                                  <b>Address:</b> {normalizedAddress || <><RequiredMark /> Required</>}
                                </Typography>
                                <Typography variant="body2" sx={{ color: normalizedPhone ? "#334155" : "#64748b" }}>
                                  <b>Phone Number:</b> {normalizedPhone || <><RequiredMark /> Required</>}
                                </Typography>
                                <Typography variant="body2" sx={{ color: selection.location ? "#334155" : "#64748b" }}>
                                  <b>Location:</b> {selection.location || <><RequiredMark /> Required</>}
                                </Typography>
                                <Typography variant="body2" sx={{ color: selection.appointmentAtMs ? "#334155" : "#64748b" }}>
                                  <b>Date & Time:</b> {selection.appointmentAtMs ? fmtDateTime(selection.appointmentAtMs) : <><RequiredMark /> Required</>}
                                </Typography>
                                <Typography variant="body2" sx={{ color: selectedLocationAddress ? "#334155" : "#64748b" }}>
                                  <b>Pickup Address:</b>{" "}
                                  {selectedLocationAddress || (selection.location ? "Exact address has not been added to location metadata yet." : <><RequiredMark /> Select a pickup location</>)}
                                </Typography>
                                {selectedLocationAddress ? (
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                    <Typography variant="body2" sx={{ color: "#334155", fontWeight: 700 }}>
                                      <b>Opens maps app:</b>
                                    </Typography>
                                    <Button
                                      component="a"
                                      href={selectedLocationGoogleMapsUrl || undefined}
                                      target="_blank"
                                      rel="noreferrer"
                                      aria-label="Open in Google Maps"
                                      sx={{
                                        ...mapIconButtonSx,
                                        background: "linear-gradient(135deg,#2563eb,#06b6d4)",
                                        "&:hover": {
                                          background: "linear-gradient(135deg,#1d4ed8,#0891b2)",
                                        },
                                      }}
                                    >
                                      <GoogleIcon sx={{ fontSize: 20 }} />
                                    </Button>
                                    <Button
                                      component="a"
                                      href={selectedLocationAppleMapsUrl || undefined}
                                      target="_blank"
                                      rel="noreferrer"
                                      aria-label="Open in Apple Maps"
                                      sx={{
                                        ...mapIconButtonSx,
                                        background: "linear-gradient(135deg,#0f172a,#334155)",
                                        "&:hover": {
                                          background: "linear-gradient(135deg,#020617,#1e293b)",
                                        },
                                      }}
                                    >
                                      <AppleIcon sx={{ fontSize: 20 }} />
                                    </Button>
                                  </Box>
                                ) : null}
                              </Box>
                            </Box>
                            {!isPickupFormComplete ? (
                              <Alert
                                severity="warning"
                                sx={{
                                  mt: 1.15,
                                  borderRadius: 2,
                                  background: "rgba(254,249,195,0.9)",
                                  border: "1px solid rgba(202,138,4,0.18)",
                                }}
                              >
                                Please complete:{" "}
                                {missingPickupFields.map((field, index) => (
                                  <React.Fragment key={field}>
                                    {index ? ", " : ""}
                                    {field} <RequiredMark />
                                  </React.Fragment>
                                ))}
                              </Alert>
                            ) : null}

                            <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                              <Button
                                onClick={() => {
                                  clearMessages();
                                  setStep(2);
                                }}
                                sx={{
                                  textTransform: "none",
                                  borderRadius: 999,
                                  px: 2.6,
                                  py: 1,
                                  fontWeight: 800,
                                  color: "#334155",
                                  background: "rgba(226,232,240,0.78)",
                                  "&:hover": { background: "rgba(203,213,225,0.92)" },
                                }}
                              >
                                Back
                              </Button>
                              <Button
                                onClick={handleSubmit}
                                disabled={submitting || !isPickupFormComplete}
                                sx={{
                                  textTransform: "none",
                                  borderRadius: 999,
                                  px: 3,
                                  py: 1.05,
                                  fontWeight: 900,
                                  color: "#fff",
                                  background: submitting || isPickupFormComplete
                                    ? "linear-gradient(90deg,#15803d,#166534)"
                                    : "linear-gradient(90deg,#9ca3af,#6b7280)",
                                  "&:hover": {
                                    background: submitting || isPickupFormComplete
                                      ? "linear-gradient(90deg,#166534,#14532d)"
                                      : "linear-gradient(90deg,#9ca3af,#6b7280)",
                                  },
                                }}
                              >
                                {submitting ? "Saving..." : isPickupFormComplete ? "Save Pickup Selection" : <>Complete Required Fields <RequiredMark /></>}
                              </Button>
                            </Box>
                          </Paper>
                        ) : null}
                      </>
                    ) : null}
                  </Paper>
                ) : null}

                {hasSavedSelection ? (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "minmax(280px, 0.9fr) minmax(360px, 1.1fr)" },
                      gap: 2,
                    }}
                  >
                    <Paper
                      sx={{
                        p: { xs: 2, md: 2.5 },
                        borderRadius: 3,
                        border: "1px solid rgba(59,130,246,0.16)",
                        background: "linear-gradient(180deg,#ffffff 0%,#eff6ff 100%)",
                        boxShadow: "0 18px 40px rgba(37,99,235,0.08)",
                      }}
                    >
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, height: "100%" }}>
                      <Chip
                        label="Auction Manager"
                        sx={{
                          alignSelf: "flex-start",
                          ...mobileChipSx,
                          fontWeight: 900,
                          color: "#1d4ed8",
                          backgroundColor: "rgba(59,130,246,0.12)",
                        }}
                      />
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.45 }}>
                        <Typography sx={{ fontWeight: 900, color: "#0f172a", fontSize: { xs: 20, md: 22 } }}>
                          You can contact your account manager in case you need any help.
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#475569", lineHeight: 1.7 }}>
                          Reach out any time if you have pickup questions, need auction help, or want support before arrival.
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2.5,
                          backgroundColor: "rgba(255,255,255,0.85)",
                          border: "1px solid rgba(148,163,184,0.2)",
                        }}
                      >
                        <Typography sx={{ fontWeight: 900, color: "#111827", fontSize: 18 }}>
                          {ACCOUNT_MANAGER.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#475569", mt: 0.45 }}>
                          {ACCOUNT_MANAGER.email}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#475569", mt: 0.2 }}>
                          {ACCOUNT_MANAGER.phone}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: "auto" }}>
                        <Button
                          component="a"
                          href={`mailto:${ACCOUNT_MANAGER.email}`}
                          sx={{
                            textTransform: "none",
                            borderRadius: 999,
                            px: 2.1,
                            width: { xs: "100%", sm: "auto" },
                            fontWeight: 900,
                            color: "#fff",
                            background: "linear-gradient(90deg,#2563eb,#1d4ed8)",
                            "&:hover": { background: "linear-gradient(90deg,#1d4ed8,#1e40af)" },
                          }}
                        >
                          Email Jeff
                        </Button>
                        <Button
                          component="a"
                          href={`tel:${ACCOUNT_MANAGER.phone.replace(/[^\d+]/g, "")}`}
                          variant="outlined"
                          sx={{
                            textTransform: "none",
                            borderRadius: 999,
                            px: 2.1,
                            width: { xs: "100%", sm: "auto" },
                            fontWeight: 900,
                            borderColor: "rgba(37,99,235,0.3)",
                            color: "#1d4ed8",
                            backgroundColor: "rgba(255,255,255,0.7)",
                          }}
                        >
                          Call Jeff
                        </Button>
                      </Box>
                    </Box>
                    </Paper>

                    <Paper
                      sx={{
                        p: { xs: 2, md: 2.5 },
                        borderRadius: 3,
                        border: "1px solid rgba(16,185,129,0.16)",
                        background: "linear-gradient(135deg,#ecfeff 0%,#ffffff 45%,#f0fdf4 100%)",
                        boxShadow: "0 22px 48px rgba(15,118,110,0.08)",
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                    <Box
                      sx={{
                        position: "absolute",
                        right: -24,
                        top: -24,
                        width: 120,
                        height: 120,
                        borderRadius: "50%",
                        background: "radial-gradient(circle, rgba(20,184,166,0.14) 0%, rgba(20,184,166,0) 72%)",
                      }}
                    />
                    <Box sx={{ position: "relative", display: "flex", flexDirection: "column", gap: 1.5 }}>
                      <Chip
                        label="Explorer"
                        sx={{
                          alignSelf: "flex-start",
                          ...mobileChipSx,
                          fontWeight: 900,
                          color: "#0f766e",
                          backgroundColor: "rgba(20,184,166,0.12)",
                        }}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: { xs: "flex-start", sm: "center" },
                          gap: 1.5,
                          flexDirection: { xs: "column", sm: "row" },
                        }}
                      >
                        <Box
                          sx={{
                            width: 72,
                            height: 72,
                            borderRadius: 2.5,
                            backgroundColor: "#fff",
                            border: "1px solid rgba(148,163,184,0.18)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 10px 24px rgba(15,23,42,0.08)",
                            flexShrink: 0,
                          }}
                        >
                          <Box
                            component="img"
                            src={EXPLORER_LOGO_URL}
                            alt="Explorer logo"
                            sx={{ width: 52, height: 52, objectFit: "contain" }}
                          />
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.35 }}>
                          <Typography sx={{ fontWeight: 900, color: "#0f172a", fontSize: { xs: 22, md: 24 }, lineHeight: 1.1 }}>
                            Are you a reseller?
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#475569", fontWeight: 700 }}>
                            Explorer by Listernaut
                          </Typography>
                        </Box>
                      </Box>
                      <Typography sx={{ color: "#0f172a", fontWeight: 900, fontSize: { xs: 17, md: 18 }, lineHeight: 1.45 }}>
                        LISTERNAUT AI cataloging software allows you to automate product titles and descriptions in seconds!
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#334155", lineHeight: 1.75 }}>
                        The all in one barcode scanner and AI product recognition works on your phone or tablet and organizes your catalogs for you all in one place for prep to launch.
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#334155", lineHeight: 1.75 }}>
                        You can connect direct to your Ebay account and have products up for sale at the click of a button.
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        {["AI titles", "Barcode scanning", "Ebay ready"].map((item) => (
                          <Chip
                            key={item}
                            label={item}
                            sx={{
                              ...mobileChipSx,
                              fontWeight: 800,
                              backgroundColor: "rgba(255,255,255,0.72)",
                              border: "1px solid rgba(20,184,166,0.14)",
                              color: "#0f766e",
                            }}
                          />
                        ))}
                      </Box>
                      <Box sx={{ display: "flex", alignItems: { xs: "stretch", sm: "center" }, justifyContent: "space-between", gap: 1.25, flexDirection: { xs: "column", sm: "row" } }}>
                        <Typography variant="body2" sx={{ color: "#475569", fontWeight: 700 }}>
                          explorer.listernaut.com
                        </Typography>
                        <Button
                          component="a"
                          href={EXPLORER_URL}
                          target="_blank"
                          rel="noreferrer"
                          sx={{
                            textTransform: "none",
                            borderRadius: 999,
                            px: 2.5,
                            py: 1,
                            width: { xs: "100%", sm: "auto" },
                            fontWeight: 900,
                            color: "#fff",
                            background: "linear-gradient(90deg,#0f766e,#14b8a6)",
                            "&:hover": { background: "linear-gradient(90deg,#115e59,#0f766e)" },
                          }}
                        >
                          Explore Explorer
                        </Button>
                      </Box>
                    </Box>
                    </Paper>
                  </Box>
                ) : null}
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}

function nextSlotsOrEmpty(value) {
  return Array.isArray(value) ? value : [];
}
